import { apiClient } from './client';

/**
 * 대표 · 부서장 지정과 **결재선 자동 도출** — 근태 5단계 (2026-08-24).
 *
 * 서버: `apps/chat-server/src/modules/approval/org-leader.routes.ts`.
 * 설계: `docs/attendance-round2-sample.html` 제안 3 · 화면 H·S.
 *
 * 사다리는 **팀장 → 부서장 → 대표**이고 빈 자리는 건너뛰고 위로 올라간다. 결재자를 하나도 못
 * 찾으면 `blocked` 다 — 빈 결재선으로 저장하면 그 문서는 영원히 PENDING 이 된다.
 */

export interface OrgLeaderDto {
  representative: { userId: string; name: string | null } | null;
  /** 회사 정보의 **표시용 이름 칸** — 결재자를 가리키지 못한다(둘의 차이를 화면이 말한다). */
  representativeName: string | null;
  divisions: Array<{ id: string; name: string; leaderUserId: string | null; leaderName: string | null }>;
  /** 팀장은 조직도(`TeamMember.isLeader`)의 것이라 **여기서는 보여만** 준다. */
  /**
   * 팀 — 팀장은 조직도의 것이라 여기서는 보여만 준다.
   * ★ `memberCount` 는 2026-08-28 에 더한 칸(**옛 서버는 안 준다**) — 빈 칸이 몇 명에게
   *   영향을 주는지 세는 데 쓴다. 없으면 0 으로 센다(팀 개수는 그래도 맞다).
   */
  teams: Array<{
    id: string; name: string; divisionId: string | null;
    leaderUserId: string | null; leaderName: string | null;
    memberCount?: number;
    /** 2026-09-13 계약 §18.4 — 그 팀의 입사 예정 인원(`memberCount` 에서는 빠진다). 옛 서버는 안 준다. */
    upcomingCount?: number;
  }>;
  users: Array<{ userId: string; name: string; position: string | null }>;
}

export interface LineSuggestionDto {
  type: string;
  defaultSteps: number;
  /**
   * 기안자가 결재선을 고칠 수 있는가 (결재 A1, 2026-08-24). **옛 서버는 안 준다** → 기본 허용.
   * 끄면 서버가 조직에서 결재선을 다시 뽑으므로, 화면에서도 고치는 길을 닫아 둔다.
   */
  allowDrafterEdit?: boolean;
  /** 단계 수가 레지스트리에서 왔나(`REGISTRY`) 코드 상수에서 왔나(`CODE`). */
  rulesSource?: 'REGISTRY' | 'CODE';
  /** 전결 기준 (결재 A3, 2026-08-24). 옛 서버는 안 준다. */
  finalize?: { kind: 'NONE' | 'DAYS' | 'AMOUNT'; threshold: number | null; steps: number };
  /** 이번 크기로 실제로 잘렸나. */
  finalized?: boolean;
  teamName: string | null;
  divisionName: string | null;
  blocked: boolean;
  notes: string[];
  /**
   * 어느 **기본 결재선**에서 왔나 (2026-09-04 · 판 P1). 없으면 `null` — 그것이 정상이고
   * 결재자·최종결재자 칸이 빈 채로 선다. 옛 서버는 이 칸을 안 준다.
   */
  defaultLine?: { id: string; name: string } | null;
  /** 기본 결재선에 딸린 참고자 — **결재를 하지 않는다**(하위 명단). 옛 서버는 안 준다. */
  referrerIds?: string[];
  lines: Array<{
    approverId: string; step: number; lineType: 'APPROVE' | 'REFER';
    /**
     * 그 줄이 앉은 자리 — **마지막 하나가 「최종결재자」**, 그 앞이 「결재자」다(이름은 2026-09-24 판).
     * ★ 옛 서버는 대신 `role`(팀장·부서장·대표)을 줬다 — 사다리를 안 쓰므로 사라진다.
     */
    slot?: 'REVIEW' | 'APPROVE';
    /** @deprecated 조직 사다리의 자리. 2026-09-04 부터 서버가 주지 않는다. */
    role?: 'TEAM_LEADER' | 'DIVISION_LEADER' | 'REPRESENTATIVE';
    name: string | null; position: string | null;
  }>;
}

/**
 * 결재선에서 그 사람이 하는 일 — 앞사람들은 순서대로 막고, 마지막 한 사람이 끝낸다.
 *
 * ⛔ **전선 값(`REVIEW` · `APPROVE`)은 «안 바꾼다» — 이름과 값이 어긋나 보이는 것이 맞다.**
 *   `REVIEW` 는 「앞 자리」, `APPROVE` 는 「마지막 자리」를 뜻하는 **내부 글자**일 뿐이고,
 *   서버(`approval/default-line.ts` 의 `LineSlot`)가 그 값으로 준다.
 *   2026-09-24 에 바뀐 것은 **사람에게 보이는 이름**뿐이라 서버·앱이 서로를 안 기다렸다.
 * ⛔ 값을 「맞춰 주려고」 바꾸지 말 것 — 바꾸면 서버·웹이 한 판이 되고 배포 창이 생긴다.
 */
export const LINE_SLOT_LABEL: Record<string, string> = { REVIEW: '결재자', APPROVE: '최종결재자' };

/** @deprecated 조직 사다리의 자리 이름. 사다리를 안 쓰게 되면서 뜻을 잃었다(판 P1). */
export const LADDER_ROLE_LABEL: Record<string, string> = {
  TEAM_LEADER: '팀장', DIVISION_LEADER: '부서장', REPRESENTATIVE: '대표',
};

export async function getOrgLeaders(): Promise<OrgLeaderDto> {
  const { data } = await apiClient.get<OrgLeaderDto>('/hr/org-leaders');
  return data;
}

export async function saveOrgLeaders(body: {
  representativeUserId?: string | null;
  divisions?: Array<{ id: string; leaderUserId: string | null }>;
}): Promise<{ ok: boolean }> {
  const { data } = await apiClient.put('/hr/org-leaders', body);
  return data;
}

/** 내 기준 자동 결재선. 옛 서버에는 없다(404) — 호출부가 조용히 넘어간다. */
/**
 * @param measure 이 문서의 크기(일수·금액) — 넘기면 **전결까지 반영된 결재선**이 온다 (결재 A3).
 *                옛 서버는 이 칸을 모르지만 `additionalProperties:false` 라 400 이 나므로
 *                **값이 있을 때만** 싣는다.
 */
export async function getLineSuggestion(type: string, measure?: number | null): Promise<LineSuggestionDto> {
  const { data } = await apiClient.get<LineSuggestionDto>('/approval/line-suggestion', {
    params: { type, ...(typeof measure === 'number' ? { measure } : {}) },
  });
  return data;
}
